import { useEffect, useRef } from 'react';

import { EMBEDDED_MENU_SOURCE, MENU_SOURCE } from '../analytics.ts';
import { useTwgAnalyticsEvents } from '../useTwgAnalyticsEvents.ts';
import { groupSmartLinksByProduct } from '../utils/groupSmartLinksByProduct.ts';
import { getMenuShownAttributes, getUnauthenticatedLinkCount } from '../utils/menuViewAnalytics.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

export type MenuSectionCounts = {
	relatedContentCount: number;
	appearsInCount: number;
	linksToCount: number;
};

export type UseMenuViewAnalyticsOptions = {
	visibleLinks: EnhancedSmartLink[];
	ready: boolean;
	source: typeof MENU_SOURCE | typeof EMBEDDED_MENU_SOURCE;
	sectionCounts?: MenuSectionCounts;
};

export const useMenuViewAnalytics = ({
	visibleLinks,
	ready,
	source,
	sectionCounts,
}: UseMenuViewAnalyticsOptions): void => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const hasFiredMenuViewedRef = useRef(false);
	const hasFiredUnauthenticatedRef = useRef(false);

	useEffect(() => {
		if (hasFiredMenuViewedRef.current || !ready) {
			return;
		}
		const menuShownAttributes = getMenuShownAttributes(groupSmartLinksByProduct(visibleLinks));
		hasFiredMenuViewedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'menu',
				actionSubjectId: MENU_SOURCE,
				source,
				attributes: { ...menuShownAttributes, ...sectionCounts },
			},
		}).fire();
	}, [createAnalyticsEvent, ready, sectionCounts, source, visibleLinks]);

	useEffect(() => {
		if (hasFiredUnauthenticatedRef.current || !ready) {
			return;
		}
		const unauthenticatedLinkCount = getUnauthenticatedLinkCount(visibleLinks);
		if (unauthenticatedLinkCount === 0) {
			return;
		}
		hasFiredUnauthenticatedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'unauthenticatedSmartlink',
				actionSubjectId: MENU_SOURCE,
				source,
				attributes: { unauthenticatedLinkCount },
			},
		}).fire();
	}, [createAnalyticsEvent, ready, source, visibleLinks]);
};
